import React, {SyntheticEvent } from 'react';
import { useSelector } from 'react-redux';
import { FormControl, FormHelperText } from '@mui/material';
import SelectWithSearch from 'src/components/SelectWithSearch/view';
import useConfirmationDialog from 'src/components/ConfirmationDialog/view';
import { getCurrentCompanyId } from 'src/modules/companies/slices';
import { useKeywordsApi } from 'src/modules/keywords/api';
import { isKeywordClaimedElsewhere, isSelectableKeyword } from '../../helpers/keywordHelpers';
import { useBuilderContext, useField } from '../../context';

type SelectOption = { text: string; value: string; hasWarning?: boolean; isHidden?: boolean };

export const KeywordsField: React.FC = () => {
  const { value: keywordIds, error, onChange } = useField('keywordIds');
  const companyId = useSelector(getCurrentCompanyId) as string;
  const { createKeyword } = useKeywordsApi();
  // Loaded once per builder open by useCampaignConfigLoader, so the picker and the phone
  // preview share one request.
  const { config, keywords, addKeyword, isCampaignReadOnly, campaignId } = useBuilderContext();
  const { openDialog, ConfirmationDialog } = useConfirmationDialog();

  const label = config?.keywords?.label || 'Keyword(s)';

  // A failure here is logged, not surfaced: the picker stays usable.
  const handleAddKeyword = async (keyword: string) => {
    if (!companyId) return;
    try {
      const response = await createKeyword(companyId, { name: keyword });
      addKeyword(response.data);
      onChange([...(keywordIds ?? []), response.data.id]);
    } catch (e) {
      console.error('Failed to create keyword', e);
    }
  };

  const options: SelectOption[] = keywords.map(keyword => ({
    text: keyword.name || '',
    value: keyword.id || '',
    hasWarning: isKeywordClaimedElsewhere(keywords, keyword.id || '', campaignId),
    isHidden: !isSelectableKeyword(keyword),
  }));

  const selected: string[] = keywordIds ?? [];

  return (
    <FormControl sx={{ width: '100%', my: 1.25 }} error={!!error}>
      <SelectWithSearch
        disabled={isCampaignReadOnly}
        label={label}
        placeholder={label}
        options={options}
        values={selected}
        handleSelectChange={(_: SyntheticEvent | undefined, items: SelectOption[]) => {
          const apply = () => onChange(items.map(s => s.value));
          // Confirm before adding a keyword another active campaign already uses.
          const lastAdded = items[items.length - 1];
          if (lastAdded?.hasWarning && items.length > selected.length) {
            openDialog({
              title: 'WARNING!',
              content:
                'This keyword is already part of another active campaign, which may cause a bad experience for the recipient. Are you sure you want to continue?',
              onConfirm: apply,
              confirmButtonText: 'Continue',
            });
            return;
          }
          apply();
        }}
        handleAddItem={handleAddKeyword}
        entity="Keyword"
        style={{ width: '100%', maxHeight: '120px' }}
      />
      <FormHelperText>{error ?? ''}</FormHelperText>
      <ConfirmationDialog />
    </FormControl>
  );
};
